import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useAppTheme } from "@/contexts/ThemeContext";
import {
  helpDismissalStorageKey,
  readDismissedVersionFromStorage,
  writeDismissedVersionToStorage,
} from "@/hooks/useHelpDismissal";
import { writeInlineHelpDismissed } from "@/components/common/inlineHelpDismissStorage";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import {
  notifyUiPrefsHydrated,
  parseUserUiPrefs,
  patchUserUiPrefs,
  rememberCachedUiPrefs,
} from "@/lib/userUiPrefs";

function applyHelpAndInlineToLocal(prefs: ReturnType<typeof parseUserUiPrefs>): void {
  for (const [panelId, version] of Object.entries(prefs.helpDismissed)) {
    const key = helpDismissalStorageKey(panelId as PrimaryMode);
    if (version > readDismissedVersionFromStorage(key)) {
      writeDismissedVersionToStorage(key, version);
    }
  }
  for (const dismissKey of Object.keys(prefs.inlineHelpDismissed)) {
    writeInlineHelpDismissed(dismissKey);
  }
}

/** After sign-in, pull account UI prefs into this origin and upload local ones. */
export function UserUiPrefsHydrator() {
  const auth = useAuth();
  const { theme, setTheme } = useAppTheme();
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const hydratedUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (auth.status !== "ready" || !auth.session?.user) {
      if (!auth.session) hydratedUserRef.current = null;
      return;
    }
    const userId = auth.session.user.id;
    if (hydratedUserRef.current === userId) return;
    hydratedUserRef.current = userId;

    const remote = parseUserUiPrefs(
      (auth.session.user.user_metadata as Record<string, unknown> | undefined)?.bb_ui,
    );
    rememberCachedUiPrefs(remote);
    applyHelpAndInlineToLocal(remote);
    const localTheme = themeRef.current;
    if (remote.theme && localTheme === "system") {
      setTheme(remote.theme);
    } else {
      void patchUserUiPrefs({ theme: localTheme });
    }
    notifyUiPrefsHydrated();
  }, [auth.status, auth.session, setTheme]);

  return null;
}
